import {clearSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import type {ClearKeysOptions} from '@github-ui/use-safe-storage/types'
import {createRef, type RefObject, useImperativeHandle, useMemo} from 'react'
import {graphql, readInlineData} from 'react-relay'

import type {IssueFormElements_templateElements$key} from './__generated__/IssueFormElements_templateElements.graphql'
import {CheckboxesElement, CheckboxesElementInternal} from './elements/CheckboxesElement'
import {DropdownElement, DropdownElementInternal} from './elements/DropdownElement'
import {MarkdownElement, MarkdownElementInternal} from './elements/MarkdownElement'
import {TextAreaElement, TextAreaElementInternal} from './elements/TextAreaElement'
import {TextInputElement, TextInputElementInternal} from './elements/TextInputElement'
import {UploadElement} from './elements/UploadElement'
import styles from './IssueFormElements.module.css'
import type {CommentBoxProps, IssueFormElement, IssueFormElementRef, IssueFormRef, SharedElementProps} from './types'
import {createUniqueIdForElement} from './utils/session-storage'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueFormElementsTemplateElementsFragment = graphql`
  fragment IssueFormElements_templateElements on IssueForm @inline {
    elements {
      __typename
      ... on IssueFormElementInput {
        ...TextInputElement_input @dangerously_unaliased_fixme
      }
      ... on IssueFormElementTextarea {
        ...TextAreaElement_input @dangerously_unaliased_fixme
      }
      ... on IssueFormElementMarkdown {
        ...MarkdownElement_input @dangerously_unaliased_fixme
      }
      ... on IssueFormElementDropdown {
        ...DropdownElement_input @dangerously_unaliased_fixme
      }
      ... on IssueFormElementCheckboxes {
        ...CheckboxesElement_input @dangerously_unaliased_fixme
      }
      ... on IssueFormElementUpload {
        ...UploadElement_input @dangerously_unaliased_fixme
      }
    }
  }
`

type CommonProps = {
  outputRef: RefObject<IssueFormRef | null>
  onSave?: () => void
  setIsFileUploading?: (value: boolean) => void
} & CommentBoxProps &
  SharedElementProps

type IssueFormElementsViaQueryProps = {
  issueFormRef: IssueFormElements_templateElements$key
} & CommonProps

export type IssueFormElementsViaPayloadProps = {
  elements: IssueFormElement[]
} & CommonProps

export const IssueFormElements = (props: IssueFormElementsViaQueryProps | IssueFormElementsViaPayloadProps) => {
  if ('elements' in props) {
    return <IssueFormElementsViaPayload {...props} />
  } else {
    return <IssueFormElementsViaQuery {...props} />
  }
}

const IssueFormElementsViaPayload = ({
  elements,
  outputRef,
  sessionStorageKey,
  ...props
}: IssueFormElementsViaPayloadProps) => {
  const inputRefs = useInputElementReferences(elements.length, outputRef)

  return (
    <div className={styles.formElementsContainer}>
      {elements.map((element, index) => {
        const storageKey = createUniqueIdForElement(sessionStorageKey, element, index)
        switch (element.type) {
          case 'markdown':
            // eslint-disable-next-line @eslint-react/no-array-index-key
            return <MarkdownElementInternal key={index} {...element} />
          case 'textarea':
            return (
              <TextAreaElementInternal
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                index={index}
                {...element}
                ref={inputRefs[index]}
                sessionStorageKey={storageKey}
                {...props}
              />
            )
          case 'input':
            return (
              <TextInputElementInternal
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                index={index}
                {...element}
                ref={inputRefs[index]}
                sessionStorageKey={storageKey}
                {...props}
              />
            )
          case 'dropdown':
            return (
              <DropdownElementInternal
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                index={index}
                {...element}
                ref={inputRefs[index]}
                sessionStorageKey={storageKey}
              />
            )
          case 'checkboxes':
            return (
              <CheckboxesElementInternal
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                index={index}
                {...element}
                ref={inputRefs[index]}
                sessionStorageKey={storageKey}
              />
            )
        }
      })}
    </div>
  )
}

const IssueFormElementsViaQuery = ({issueFormRef, outputRef, subject, ...props}: IssueFormElementsViaQueryProps) => {
  // eslint-disable-next-line no-restricted-syntax
  const data = readInlineData<IssueFormElements_templateElements$key>(
    IssueFormElementsTemplateElementsFragment,
    issueFormRef,
  )

  const inputRefs = useInputElementReferences(data.elements.length, outputRef)

  return (
    <div className={styles.formElementsContainer}>
      {data.elements.map((element, index) => {
        switch (element.__typename) {
          case 'IssueFormElementMarkdown':
            return (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <div key={index} className={styles.markdownElementWrapper}>
                <MarkdownElement elementRef={element} />
              </div>
            )
          case 'IssueFormElementUpload':
            if ('repository' in subject && subject.repository) {
              return (
                <UploadElement
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={index}
                  index={index}
                  subject={subject}
                  elementRef={element}
                  ref={inputRefs[index]}
                  {...props}
                />
              )
            }

            return null
          case 'IssueFormElementInput':
            // eslint-disable-next-line @eslint-react/no-array-index-key
            return <TextInputElement key={index} index={index} elementRef={element} ref={inputRefs[index]} {...props} />
          case 'IssueFormElementDropdown':
            // eslint-disable-next-line @eslint-react/no-array-index-key
            return <DropdownElement key={index} index={index} elementRef={element} ref={inputRefs[index]} {...props} />
          case 'IssueFormElementTextarea':
            return (
              <TextAreaElement
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                index={index}
                elementRef={element}
                subject={subject}
                ref={inputRefs[index]}
                {...props}
              />
            )
          case 'IssueFormElementCheckboxes':
            return (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <CheckboxesElement key={index} index={index} elementRef={element} ref={inputRefs[index]} {...props} />
            )
          default:
            return null
        }
      })}
    </div>
  )
}

const useInputElementReferences = (elementCount: number, outputRef: RefObject<IssueFormRef | null>) => {
  const inputRefs = useMemo<Array<RefObject<IssueFormElementRef | null>>>(
    // eslint-disable-next-line @eslint-react/no-create-ref
    () => Array.from({length: elementCount}, () => createRef()),
    [elementCount],
  )

  useImperativeHandle(
    outputRef,
    () => ({
      markdown: () =>
        inputRefs
          .map(ref => ref.current?.markdown() ?? '')
          .filter(markdown => markdown.length > 0)
          .join('\n\n'),
      getInvalidInputs: () =>
        inputRefs.map(ref => ref.current).filter((ref): ref is IssueFormElementRef => ref !== null && !ref.validate()),
      validateInputs: () => !inputRefs.map(ref => ref.current?.validate() ?? true).some(isValid => !isValid),
      resetInputs: (options = {}) => {
        const {textOnly = false} = options
        for (const ref of inputRefs) {
          if (!textOnly) {
            ref.current?.reset()
            continue
          }

          if (ref.current?.type === 'input' || ref.current?.type === 'textarea' || ref.current?.type === 'markdown') {
            ref.current?.reset()
          }
        }

        // Also clear the local storage
        outputRef.current?.clearSessionStorage()
      },
      hasChanges: () => inputRefs.some(ref => ref.current?.hasChanges() ?? false),
      clearSessionStorage: (options?: ClearKeysOptions) => {
        const storageKeys = inputRefs
          .map(ref => ref.current?.getSessionStorageKey())
          .filter((k): k is string => k !== null)

        clearSessionStorage(storageKeys, options)
      },
    }),
    [inputRefs, outputRef],
  )

  return inputRefs
}
